import {
  useCallback,
  useEffect,
  useRef,
  type ReactElement,
  type ReactNode,
} from "react";
import { useReactFlow } from "@xyflow/react";
import { SOLVE_PHASES, type SolvePhase } from "@playbookai/types";
import { ArrowLeft, ChevronRight } from "lucide-react";
import {
  Stepper,
  WorkspaceControlButton,
  WorkspaceControlGroup,
  useCameraDuration,
} from "@playbookai/ui";
import { ReturnPhaseControl } from "./ReturnPhaseControl.js";
import { PHASE_META, phaseRect } from "./phase-metadata.js";
import { phaseAreaFitRect, type PhaseAreaLayout } from "./phase-areas.js";

const SOLVE_START_THRESHOLDS = {
  labelsVisibleAt: 920,
  overflowAt: 700,
  stackedPhoneAt: 640,
} as const;

/**
 * Floating SOLVE phase strip — back, the revealed phase circles, and Advance,
 * pinned top-start as a `WorkspaceControlGroup`. Clicking a circle animates
 * the ReactFlow viewport to that phase's lane. Jump-only: the per-phase lock
 * affordance lives on the swimlane lane-header (`PhaseSwimlanes`), not here
 * (ADR-0019).
 *
 * Mounted via `<Canvas>`'s `hudSlot` (inside the ReactFlow provider, so
 * `useReactFlow()` works); the group positions itself at top-4 start-4.
 */
export function SolvePhaseStrip({
  phases = SOLVE_PHASES,
  areas,
  currentPhase,
  deferPhaseFraming = false,
  phaseFramingRequest = 0,
  onBack,
  canAdvance = false,
  advancing = false,
  onAdvance,
  onReturn,
  completeAction,
}: {
  readonly phases?: readonly SolvePhase[];
  /**
   * Live rect per phase. Absent falls back to the historical fixed lane, so a
   * jump always frames the area a reader can actually see — never a
   * index-derived rect a resize has moved out from under.
   */
  readonly areas?: PhaseAreaLayout | undefined;
  readonly currentPhase?: SolvePhase | undefined;
  /** Widget navigation owns the initial camera; phase changes still frame normally. */
  readonly deferPhaseFraming?: boolean;
  /** Increment to frame the current phase after an unsuccessful widget link. */
  readonly phaseFramingRequest?: number;
  readonly onBack?: () => void;
  readonly canAdvance?: boolean;
  readonly advancing?: boolean;
  readonly onAdvance?: () => void;
  readonly onReturn?: () => void;
  /**
   * The terminal-phase action, rendered in the Advance position (PB-5272).
   *
   * A SLOT rather than an `onComplete` callback: completion opens a
   * confirmation popover that owns its own server read and mutation, and this
   * strip is presentational — it decides WHERE the action sits, never what it
   * does. Absent at every phase but the last, and for a caller who may not
   * complete.
   */
  readonly completeAction?: ReactNode;
}): ReactElement {
  const rf = useReactFlow();
  const lastFocusedPhase = useRef<SolvePhase | null>(null);
  const lastFramingRequest = useRef(phaseFramingRequest);
  /* A phase jump re-frames the whole canvas, so it is `framing`. ReactFlow
     animates this in JavaScript, which the `prefers-reduced-motion` block in
     `theme.css` cannot reach — the hook returns 0 there and the camera arrives
     at the phase without travelling (ADR-0091). */
  const framingDuration = useCameraDuration("framing");

  const jumpTo = useCallback(
    (phase: SolvePhase): void => {
      const area = areas?.get(phase);
      rf.fitBounds(area ? phaseAreaFitRect(area) : phaseRect(phase), {
        duration: framingDuration,
        padding: 0.1,
      });
    },
    [areas, framingDuration, rf],
  );

  useEffect(() => {
    const framingRequested = lastFramingRequest.current !== phaseFramingRequest;
    if (
      currentPhase === undefined ||
      (lastFocusedPhase.current === currentPhase && !framingRequested)
    ) {
      return;
    }
    lastFocusedPhase.current = currentPhase;
    lastFramingRequest.current = phaseFramingRequest;
    if (framingRequested || !deferPhaseFraming) jumpTo(currentPhase);
  }, [currentPhase, deferPhaseFraming, phaseFramingRequest, jumpTo]);

  const activeIndex = currentPhase ? SOLVE_PHASES.indexOf(currentPhase) : -1;
  const nextPhase = SOLVE_PHASES[activeIndex + 1];
  const previousPhase = SOLVE_PHASES[activeIndex - 1];

  return (
    <WorkspaceControlGroup
      placement="top-start"
      label="SOLVE navigation"
      thresholds={SOLVE_START_THRESHOLDS}
      data-geometry="solve-workspace-top-start"
      data-tour-id="solve.phases"
      className="max-sm:max-w-[calc(100%-2rem)] max-sm:flex-wrap"
      railControls
    >
      {onBack ? (
        <WorkspaceControlButton
          icon={<ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />}
          label="Back to SOLVE overview"
          tooltip="Back to overview"
          iconOnly
          shape="circle"
          onClick={onBack}
        />
      ) : null}
      <nav
        aria-label="SOLVE phases"
        data-testid="solve-phase-strip"
        className="flex items-center gap-1 rounded-full bg-pb-card/45 p-0.5"
      >
        {/* Today's look on the DS Stepper: each phase is a disc in its own
            accent with its letter, no connectors, and the current phase
            carries the selection glow (`variant="swatch"`). */}
        <Stepper
          variant="swatch"
          steps={phases.map((phase) => ({
            value: phase,
            label: `${phase} — ${PHASE_META[phase].subtitle}`,
            marker: PHASE_META[phase].letter,
            color: PHASE_META[phase].accent,
          }))}
          value={currentPhase ?? ""}
          // Every phase stays freely jumpable in either direction (never a
          // one-way wizard), so a visited phase must not read as "complete"
          // (a green check) — only the current phase differs from the rest.
          completed={[]}
          onValueChange={(value) => jumpTo(value as SolvePhase)}
          clickable="all"
          labelHidden
        />
      </nav>
      {onReturn && previousPhase ? (
        <ReturnPhaseControl
          key={currentPhase}
          previousPhase={previousPhase}
          disabled={advancing}
          onReturn={onReturn}
        />
      ) : null}
      {canAdvance && nextPhase && onAdvance ? (
        <WorkspaceControlButton
          icon={<ChevronRight className="h-4 w-4" aria-hidden />}
          iconPosition="end"
          label={advancing ? "Advancing…" : `Advance to ${nextPhase}`}
          variant="primary"
          disabled={advancing}
          onClick={onAdvance}
        />
      ) : /* At the terminal phase there is nothing to advance TO, which is
            where the SOLVE's completion action belongs — the same position
            the reader has been pressing to move forward all along. `nextPhase`
            is the condition, so the two can never render together. */
      nextPhase === undefined && completeAction ? (
        completeAction
      ) : null}
    </WorkspaceControlGroup>
  );
}
